/**
 * Internal Roles API Service
 * Handles admin/internal-roles endpoints
 */

import axiosInstance from '@/lib/axios'
import API_ROUTES from '@/lib/api-routes'
import type {
    CreateInternalRoleRequest,
    InternalRoleDetailResponse,
    InternalRoleFilters,
    InternalRoleListResponse,
    InternalRolePermissionsResponse,
    InternalRoleResponse,
    UpdateInternalRoleRequest,
} from '@/types/internal'

/**
 * Get all internal roles with filters
 * GET /admin/internal-roles
 */
export const getInternalRoles = async (
    filters?: InternalRoleFilters
): Promise<InternalRoleListResponse> => {
    const response = await axiosInstance.get<InternalRoleListResponse>(
        API_ROUTES.INTERNAL_ROLES.LIST,
        { params: filters }
    )
    return response.data
}

/**
 * Get a single internal role by ID
 * GET /admin/internal-roles/{id}
 */
export const getInternalRoleById = async (
    id: string
): Promise<InternalRoleDetailResponse> => {
    const response = await axiosInstance.get<InternalRoleDetailResponse>(
        API_ROUTES.INTERNAL_ROLES.DETAIL(id)
    )
    return response.data
}

/**
 * Create a new internal role
 * POST /admin/internal-roles
 */
export const createInternalRole = async (
    data: CreateInternalRoleRequest
): Promise<InternalRoleResponse> => {
    const response = await axiosInstance.post<InternalRoleResponse>(
        API_ROUTES.INTERNAL_ROLES.CREATE,
        data
    )
    return response.data
}

/**
 * Update an internal role (title/description/status/permissions)
 * PATCH /admin/internal-roles/{id}
 */
export const updateInternalRole = async (
    id: string,
    data: UpdateInternalRoleRequest
): Promise<InternalRoleResponse> => {
    const response = await axiosInstance.patch<InternalRoleResponse>(
        API_ROUTES.INTERNAL_ROLES.UPDATE(id),
        data
    )
    return response.data
}

/**
 * Delete an internal role
 * DELETE /admin/internal-roles/{id}
 */
export const deleteInternalRole = async (
    id: string
): Promise<InternalRoleResponse> => {
    const response = await axiosInstance.delete<InternalRoleResponse>(
        API_ROUTES.INTERNAL_ROLES.DELETE(id)
    )
    return response.data
}

/**
 * Get all available permissions
 * GET /admin/internal-roles/permissions/all
 */
export const getInternalRolePermissions =
    async (): Promise<InternalRolePermissionsResponse> => {
        const response =
            await axiosInstance.get<InternalRolePermissionsResponse>(
                API_ROUTES.INTERNAL_ROLES.PERMISSIONS
            )
        return response.data
    }
